import { useState, useEffect } from "react";
import { apiClient } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar, Clock, BarChart } from "lucide-react";
import artieLogo from '../../assets/images/artie.png';
import { useNavigate } from "react-router-dom";

export default function BookingWindowsPage() {
  const [windows, setWindows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadWindows() {
      try {
        const data = await apiClient.listBookingWindows();
        console.log("🔍 FRONTEND DEBUG: Booking windows from backend (already filtered):", data);
        
        // Backend now handles filtering - no need to filter here
        // Just display all windows returned by the backend
        setWindows(data);
      } catch (error) {
        console.error("Failed to load booking windows", error);
      } finally {
        setIsLoading(false);
      }
    }
    loadWindows();
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 py-12 px-4 sm:px-6 lg:px-8 font-geist">
      <div className="max-w-7xl mx-auto">
        
        <div className="text-center mb-12">
          <img src={artieLogo} alt="Artie" className="w-16 h-16 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-neutral-900">Active Booking Windows</h1>
          <p className="text-neutral-500 mt-2 text-lg">Select a window to submit your campaign requirements.</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-neutral-900 mx-auto"></div>
            <p className="mt-4 text-neutral-500">Loading available windows...</p>
          </div>
        ) : windows.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-sm border border-neutral-200">
            <Calendar className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-neutral-900">No Active Windows</h3>
            <p className="text-neutral-500 mt-2">There are currently no open booking windows. Please check back later.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {windows.map((window) => (
              <BookingWindowCard key={window.id} window={window} onSelect={() => navigate(`/campaigns/create?window=${window.id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BookingWindowCard({ window, onSelect }: { window: any; onSelect: () => void }) {
  const now = new Date().getTime();
  const endTime = new Date(window.submission_end_date).getTime();
  const timeDiff = endTime - now;
  
  // Use backend's is_active flag (true only when status is OPEN)
  // Display the actual admin-set status, not calculated status
  const canCreateCampaign = window.is_active; // Only true when status is OPEN
  
  // Only show "Closing Soon" if window is OPEN and actually within 7 days of closing
  const isClosingSoon = window.status.toLowerCase() === 'open' && timeDiff > 0 && timeDiff < 7 * 24 * 60 * 60 * 1000;
  
  // Get status display info based on actual admin-set status and date validity
  const getStatusInfo = (status: string, isActive: boolean) => {
    switch (status.toLowerCase()) {
      case 'open':
        if (isActive) {
          return {
            label: 'Open Now',
            variant: 'default' as const,
            className: 'bg-green-500 hover:bg-green-600 text-white border-none'
          };
        } else {
          return {
            label: 'Closed',
            variant: 'destructive' as const,
            className: 'bg-red-500 hover:bg-red-600 text-white border-none'
          };
        }
      case 'closed':
        return {
          label: 'Closed',
          variant: 'destructive' as const,
          className: 'bg-red-500 hover:bg-red-600 text-white border-none'
        };
      case 'draft':
        return {
          label: 'Draft',
          variant: 'outline' as const,
          className: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'
        };
      case 'packaging':
        return {
          label: 'Packaging',
          variant: 'secondary' as const,
          className: 'bg-blue-500 hover:bg-blue-600 text-white border-none'
        };
      case 'presentation':
        return {
          label: 'Presentation',
          variant: 'secondary' as const,
          className: 'bg-purple-500 hover:bg-purple-600 text-white border-none'
        };
      default:
        return {
          label: status,
          variant: 'outline' as const,
          className: 'bg-gray-100 hover:bg-gray-200 text-gray-700 border-gray-300'
        };
    }
  };
  
  const statusInfo = getStatusInfo(window.status, canCreateCampaign);
  
  // Debug logging for card display logic
  console.log(`🔍 FRONTEND CARD DEBUG: ${window.name} - status: ${window.status}, canCreateCampaign: ${canCreateCampaign}, isClosingSoon: ${isClosingSoon}, timeDiff: ${timeDiff}`);
  
  return (
    <div className="bg-white rounded-xl border border-neutral-200 overflow-hidden hover:border-neutral-900 transition-all duration-300 group shadow-sm hover:shadow-md flex flex-col h-full">
      <div className="p-6 flex flex-col flex-grow">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-bold text-neutral-900 group-hover:text-black transition-colors">{window.name}</h3>
            <div className="flex items-center mt-2 text-sm text-neutral-500">
              <Clock className="w-4 h-4 mr-1.5 text-neutral-400 group-hover:text-yellow-600 transition-colors" />
              <span>Closes {format(new Date(window.submission_end_date), "MMM d, yyyy")}</span>
            </div>
          </div>
          {isClosingSoon ? (
            <div className="flex flex-col items-end gap-1">
              <Badge className={`${statusInfo.className} font-medium whitespace-nowrap flex-shrink-0`}>
                {statusInfo.label}
              </Badge>
              <Badge className="bg-yellow-500 hover:bg-yellow-600 text-black border-none font-medium whitespace-nowrap flex-shrink-0 text-xs">
                Closing Soon
              </Badge>
            </div>
          ) : (
            <Badge className={`${statusInfo.className} font-medium whitespace-nowrap flex-shrink-0`}>
              {statusInfo.label}
            </Badge>
          )}
        </div>

        <div className="space-y-3 mb-6">
          {window.presentation_date && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-neutral-500">Presentation Date</span>
              <span className="font-medium text-neutral-900">{format(new Date(window.presentation_date), "MMM d, yyyy")}</span>
            </div>
          )}
          <div className="flex items-center justify-between text-sm">
            <span className="text-neutral-500">Eligible Projects</span>
            <div className="flex items-center font-medium text-neutral-900">
              <BarChart className="w-4 h-4 mr-1.5 text-yellow-600" />
              {window.eligible_project_count || 0}+
            </div>
          </div>
        </div>

        <div className="bg-neutral-50/50 rounded-lg p-4 mb-6 border border-neutral-100 flex-grow">
          <p className="text-sm text-neutral-600">
            Submit your requirements now to be included in the upcoming package composition cycle.
          </p>
        </div>

        <div className="mt-auto">
          <Button
            onClick={onSelect}
            disabled={!canCreateCampaign}
            className="w-full bg-neutral-900 text-white hover:bg-neutral-800 shadow-sm hover:shadow transition-all font-medium py-6 disabled:bg-neutral-400 disabled:cursor-not-allowed"
          >
            {canCreateCampaign ? 'Submit Requirements' : `Cannot Submit - ${statusInfo.label}`}
          </Button>
        </div>
      </div>
    </div>
  );
}
